/**
 * 多人对战三张配置表的界面侧类型与纯函数：匹配与房间参数、战阶、道具卡。
 *
 * 形状与服务端一致（camelCase 直传 PUT），校验规则的权威在服务端
 * （game/room/match-config.ts、game/ability-effects.ts）；这里只做「填的时候就能看见」的那几条。
 * 方案：https://portwind.jp.larksuite.com/wiki/Qc2kw7RpCia0PskxXrzjpiDjpBh
 */
import { ABILITY_EFFECTS } from "../../../src/game/ability-effects.ts";
import type { Translate } from "./i18n";
import type { Money } from "./big.ts";
import type { I18nText } from "./locales";

export interface MatchMode {
  mode: string;
  roomMax: number;
  minHumans: number;
  botFillWaitMs: number;
  matchStartMinMs?: number | null;
  matchStartMaxMs?: number | null;
  tiers: number[];
  segments: Array<{ id: string; tiers: number[] }>;
  widenSteps: Array<{ afterMs: number; bps: number }>;
  downgradeAfterMs: number;
  turnTimeoutMs: number;
  turnForfeitStrikes: number;
  readyTimeoutMs: number;
  minClientVersion: string;
  npcBestPct: number;
  /** null = 这个玩法不做经济（不收门票、不发名次奖）。与 prizeSplitBps / prizeBasis 同进同退 */
  ticketBps: number | null;
  prizeSplitBps: Record<string, number[]> | null;
  prizeBasis: "seat" | "stake" | null;
  /** 棋盘玩法（ludo）每手掷几枚骰子，1～3 */
  dicePerRoll: number;
  /** 一局最多走多少手，到了强制结束 */
  maxTurns: number;
  /** **每一枚**到家所需骰子总和的下限（到家 k 枚就要求 k 倍）；0 = 不设门槛 */
  minFinishSteps: number;
  /** 棋盘玩法的道具规则版本：v4.5 选完当场生效；v5.5 再加连锁结算。新开的局生效，切 v5.5 要同时抬最低客户端版本 */
  itemRules: "v4.5" | "v5.5" | "v6.1" | "v6.6" | "v7.2";
  /** 棋盘规则版本：v4.2 现行；v5.3 = 绕行 52 格、颜色每局随机、传送门非法目标拦截。新开的局生效，切 v5.3 要抬最低客户端版本并按 v5.3 重配格位 */
  boardRules: "v4.2" | "v5.3";
  /** 点数概率（2026-10-23）：standard 真实骰子（默认）；uniform_sum 点数和等概率；balanced（10-28）每人一袋全部组合洗乱抽，各人总步数拉平。新开的局生效，客户端不用改 */
  diceDist?: "standard" | "uniform_sum" | "balanced";
  /**
   * 第一名额外奖励（2026-10-20）：1 个道具 = 多少金币，只用来把第一名的金币名次奖换算成个数（向下取整，不够 1 个不发）。
   * 某种留空 = 不展示、不发；整项 null = 没有额外奖励。物品卡比例与卡包同进同退
   */
  topBonus?: TopBonus | null;
}
export interface TopBonus { dice: number | null; wildcard: number | null; itemCard: number | null; itemCardPack: string | null }
/**
 * 最高奖励四种奖励的图（cfg_main_common.top_bonus_img，2026-10-21）：全局一套，各玩法共用，随全局参数一起存。
 * null = 没传；物品卡没传时服务端下发所选卡包的封面
 */
export type TopBonusImgKind = "coin" | "dice" | "item_card" | "wildcard";
export type TopBonusImg = Record<TopBonusImgKind, string | null>;
export const EMPTY_TOP_BONUS_IMG: TopBonusImg = { coin: null, dice: null, item_card: null, wildcard: null };

/** 升阶奖励的一项，形状同其余奖励配置（服务端 game/ability-effects.ts 的 StageRewardItem）。pet 的 ref 是角色编号、数量恒 1 */
export interface StageReward { kind: string; ref?: string | null; qty?: number | null }

export interface BattleStage {
  stage: number;
  name: string;
  nameI18n: I18nText | null;
  winsRequired: number;
  /** 升到这一阶时发的奖励（《道具卡配置表 V4.1》第 5 / 10 / 15 / 20 阶「猫狗 ×1」）；空 = 不发 */
  rewards: StageReward[];
}

export interface AbilityCard {
  cardId: string;
  name: string;
  nameI18n: I18nText;
  imageUrl: string;
  effectKind: string;
  effectParams: Record<string, number | string> | null;
  unlockStage: number;
  unlockCostAsset: string;
  /** 原样往返，界面不编辑；DECIMAL，读出是十进制字符串 */
  unlockCost: Money;
  sortOrder: number;
  enabled: boolean;
}

/**
 * 已登记的效果类型：直接用服务端那份（零依赖模块），原先手抄的 21 张漏了 V4.5 追加的 5 张。登记 ≠ 局内已实现
 */
export { ABILITY_EFFECTS };
/** 解锁能花的资产（与服务端 ABILITY_COST_ASSETS 同值） */
export const COST_ASSETS = ["coins", "gems", "dice"] as const;
/** 与服务端 game-logic.ts 的 GAME_LOGICS 同值。开发期的占位玩法 dice_race 已下线 */
export const GAME_MODES = ["ludo"] as const;
export const MAX_DICE_PER_ROLL = 3;
/** 与服务端 match-config.ts 的 MIN/MAX_ROOM_SIZE 同值：座位 2 起开、4 封顶 */
export const ROOM_MIN = 2;
export const ROOM_MAX = 4;
export const ABILITY_CARD_MAX = 200;
export const BATTLE_STAGE_MAX = 50;

/** 规则 v2.5 §1.4 的默认值，只给「新增玩法」那一下用；运营改成什么就是什么 */
/** 棋盘玩法每人几枚棋子（服务端 src/game/room/ludo.ts 的 PIECES_PER_PLAYER） */
export const LUDO_PIECES = 3;
/**
 * 一枚棋子从起步走到家要几格：v4.0 布局里「本色漩涡 = 到达点 = (起步 + 51) % 56」，
 * 到家就停住、不走家门那几格，所以是 51。
 */
export const LUDO_LAP_CELLS = 51;
/** 棋盘 v5.3（2026-10-13）：绕行 52 格到达本色漩涡 */
export const LUDO_LAP_CELLS_V53 = 52;

/**
 * 估一局大概要走多少手，给「最多手数」当参照。
 *
 *   每人要掷的手数 = 三枚 × 一圈 51 格 ÷ 每手平均步数（每枚骰子期望 3.5）
 *   一局的手号是全局递增的（不分玩家），所以再乘座位数
 *   最后留 1.3 倍余量：被撞回出发点、漩涡退格都会把已经走的格数还回去
 *
 * 只是个参照——真实局还受道具、撞子频率影响，配得比它小基本每局都会打到上限、
 * 按（到家枚数 → 当前那枚的进度）判名次，很少有人真走完。
 */
export function ludoTurnsEstimate(m: Pick<MatchMode, "dicePerRoll" | "roomMax"> & { boardRules?: MatchMode["boardRules"] }): number {
  const stepsPerTurn = Math.max(1, m.dicePerRoll) * 3.5;
  const turnsPerSeat = (LUDO_PIECES * (m.boardRules === "v5.3" ? LUDO_LAP_CELLS_V53 : LUDO_LAP_CELLS)) / stepsPerTurn;
  return Math.ceil(turnsPerSeat * Math.max(1, m.roomMax) * 1.3);
}

export const newMatchMode = (mode: string): MatchMode => ({
  mode,
  roomMax: 4, minHumans: 2, botFillWaitMs: 30_000, matchStartMinMs: 1_000, matchStartMaxMs: 3_000,
  tiers: [], segments: [], widenSteps: [{ afterMs: 0, bps: 2000 }],
  downgradeAfterMs: 0, turnTimeoutMs: 30_000, turnForfeitStrikes: 3, readyTimeoutMs: 30_000,
  minClientVersion: "", npcBestPct: 85,
  ticketBps: null, prizeSplitBps: null, prizeBasis: null,
  // 棋盘玩法默认两枚；占位玩法一枚。产品给数之前 200 手封顶、不设到家门槛（三子之后够不够看 ludoTurnsEstimate）
  dicePerRoll: mode === "ludo" ? 2 : 1, maxTurns: 200, minFinishSteps: 0, itemRules: "v6.6", boardRules: "v5.3", diceDist: "standard",
});

export const newBattleStage = (xs: BattleStage[]): BattleStage => {
  const last = xs.reduce((m, s) => Math.max(m, s.stage), 0);
  const lastWins = xs.reduce((m, s) => Math.max(m, s.winsRequired), 0);
  return { stage: last + 1, name: "", nameI18n: null, winsRequired: lastWins + 1, rewards: [] };
};

export const newAbilityCard = (xs: AbilityCard[]): AbilityCard => ({
  cardId: "", name: "", nameI18n: { en: "" }, imageUrl: "",
  effectKind: ABILITY_EFFECTS[0], effectParams: null,
  unlockStage: 0, unlockCostAsset: COST_ASSETS[0], unlockCost: 0,
  sortOrder: xs.reduce((m, c) => Math.max(m, c.sortOrder), 0) + 1, enabled: true,
});

/** 界面上能当场看出来的几条；完整规则在服务端，保存时会一并报。文案走界面语言字典，所以要传 t */
export function matchModeIssues(m: MatchMode, t: Translate): string[] {
  const out: string[] = [];
  if (!Number.isInteger(m.roomMax) || m.roomMax < ROOM_MIN || m.roomMax > ROOM_MAX) out.push(t("mmode.issue.roomMax", { min: ROOM_MIN, max: ROOM_MAX }));
  if (m.minHumans < 1 || m.minHumans > m.roomMax) out.push(t("mmode.issue.minHumans"));
  const lo = m.matchStartMinMs, hi = m.matchStartMaxMs;
  if ((lo == null) !== (hi == null) || (lo != null && hi != null && (!Number.isInteger(lo) || !Number.isInteger(hi) || lo < 1000 || lo > hi || hi > 2147483647))) out.push(t("mmode.issue.startRange"));
  if (m.turnTimeoutMs <= 7000) out.push(t("mmode.issue.turnTimeout"));
  if (!Number.isInteger(m.dicePerRoll) || m.dicePerRoll < 1 || m.dicePerRoll > MAX_DICE_PER_ROLL) out.push(t("mmode.issue.dicePerRoll", { max: MAX_DICE_PER_ROLL }));
  if (!Number.isInteger(m.maxTurns) || m.maxTurns < 1) out.push(t("mmode.issue.maxTurns"));
  if (!Number.isInteger(m.minFinishSteps) || m.minFinishSteps < 0) out.push(t("mmode.issue.minFinishSteps"));
  if (m.tiers.length > 0) {
    const covered = new Set(m.segments.flatMap((s) => s.tiers));
    const missing = m.tiers.filter((x) => !covered.has(x));
    if (missing.length) out.push(t("mmode.issue.tierUncovered", { tiers: missing.join(t("mmode.listSep")) }));
    if (m.segments.length === 0) out.push(t("mmode.issue.needSegment"));
    // 下面三条服务端也拒；管端边改边存，不在这里先说，运营只会看到「保存失败」
    const ids = m.segments.map((s) => s.id);
    if (ids.some((id) => !id)) out.push(t("mmode.issue.segmentNoId"));
    const dup = ids.filter((id, i) => id && ids.indexOf(id) !== i);
    if (dup.length) out.push(t("mmode.issue.segmentDupId", { ids: [...new Set(dup)].join(t("mmode.listSep")) }));
    const empty = m.segments.filter((s) => s.tiers.length === 0).map((s) => s.id || "?");
    if (empty.length) out.push(t("mmode.issue.segmentEmpty", { ids: empty.join(t("mmode.listSep")) }));
    // 段位里写了档位表没有的数：点选界面只给已登记的档位画按钮，不在这里说就既看不见也删不掉
    const known = new Set(m.tiers);
    const stray = [...new Set(m.segments.flatMap((s) => s.tiers).filter((x) => !known.has(x)))];
    if (stray.length) out.push(t("mmode.issue.segmentStray", { tiers: stray.join(t("mmode.listSep")) }));
  } else if (m.segments.length > 0) out.push(t("mmode.issue.segmentsWithoutTiers"));
  for (let i = 1; i < m.widenSteps.length; i++) {
    if (m.widenSteps[i].afterMs <= m.widenSteps[i - 1].afterMs) { out.push(t("mmode.issue.widenOrder")); break; }
  }
  for (let i = 1; i < m.widenSteps.length; i++) {
    if (m.widenSteps[i].bps < m.widenSteps[i - 1].bps) { out.push(t("mmode.issue.widenNarrow")); break; }
  }
  if (m.widenSteps.some((w) => w.bps < 0 || w.bps > 10_000)) out.push(t("mmode.issue.widenRange"));
  const hasT = m.ticketBps !== null, hasS = m.prizeSplitBps !== null;
  if (hasT !== hasS) out.push(t("mmode.issue.ticketSplitPair"));
  if (hasT && m.prizeBasis === null) out.push(t("mmode.issue.needBasis"));
  const tb = m.topBonus;
  if (tb) {
    if ([tb.dice, tb.wildcard, tb.itemCard].some((v) => v !== null && !(Number.isSafeInteger(v) && v > 0))) out.push(t("mmode.issue.topBonusRate"));
    if ((tb.itemCard !== null) !== !!tb.itemCardPack) out.push(t("mmode.issue.topBonusPack"));
    if (!hasT && [tb.dice, tb.wildcard, tb.itemCard].some((v) => v !== null)) out.push(t("mmode.issue.topBonusNoEcon"));
  }
  // 服务端不拦这条：门票 = 全场最低档 × 比例，没有档位就没有「档」，roomTicket 算出 null、一分钱都不收。
  // 运营配了 80% 却看到没人交票，多半就是这里
  if (hasT && m.tiers.length === 0) out.push(t("mmode.issue.ticketNoTiers"));
  if (hasS) {
    // 两头都钉住再循环：最少真人填个 -1e8，从那儿数到 4 就是一亿次，页面当场卡死（上面已经出了红字）
    // 最少真人 1 = 每局 1 真人 + NPC 坐满，只有座位数那一行
    const bounded = Number.isInteger(m.minHumans) && Number.isInteger(m.roomMax) && m.minHumans >= 1 && m.roomMax <= ROOM_MAX;
    for (let n = m.minHumans === 1 ? m.roomMax : m.minHumans; bounded && n <= m.roomMax; n++) {
      const row = m.prizeSplitBps![String(n)];
      if (!row) out.push(t("mmode.issue.splitRowMissing", { n }));
      else if (row.length !== n) out.push(t("mmode.issue.splitRowLength", { n }));
      else if (row.reduce((a, b) => a + b, 0) > 10_000) out.push(t("mmode.issue.splitRowOver", { n }));
    }
  }
  return out;
}

export function battleStageIssues(xs: BattleStage[], t: Translate): string[] {
  const out: string[] = [];
  const sorted = [...xs].sort((a, b) => a.stage - b.stage);
  sorted.forEach((s, i) => { if (s.stage !== i + 1) out.push(t("mmode.issue.stageSeq")); });
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].winsRequired <= sorted[i - 1].winsRequired) { out.push(t("mmode.issue.stageWinsAsc")); break; }
  }
  return [...new Set(out)];
}

export function abilityCardIssues(cs: AbilityCard[], stages: BattleStage[], t: Translate): string[] {
  const out: string[] = [];
  const ids = new Set<string>();
  const stageSet = new Set(stages.map((s) => s.stage));
  for (const c of cs) {
    if (!/^[a-z0-9_-]{1,64}$/.test(c.cardId)) out.push(t("mmode.issue.cardIdFormat", { id: JSON.stringify(c.cardId) }));
    else if (ids.has(c.cardId)) out.push(t("mmode.issue.cardIdDup", { id: c.cardId }));
    else ids.add(c.cardId);
    if (!c.nameI18n?.en) out.push(t("mmode.issue.cardNoEn", { id: c.cardId || t("mmode.issue.unnamed") }));
    if (c.unlockStage > 0 && !stageSet.has(c.unlockStage)) out.push(t("mmode.issue.cardBadStage", { id: c.cardId, stage: c.unlockStage }));
    // 没有单独的「引导阶」：引导就是第 1 阶。每张启用的卡都要挂在某一阶上，没挂的发布闸 ABILITY_CARD_NO_STAGE 会拦（别放宽，见 src/app/ability.ts abilityTreeFrom）
    else if (c.enabled && stageSet.size > 0 && c.unlockStage === 0) out.push(t("mmode.issue.cardNoStage", { id: c.cardId || t("mmode.issue.unnamed") }));
  }
  return out;
}
